"use client";

import { InnerContainer, MainContainer } from "@/components/Containers";
import { FallbackMainContainerAdSlot } from "@/features/Ads/components/MainContainerAdSlot";
import SetEditingPopups from "./components/popups/SetEditingPopups";
import SetEditingHeader from "./components/SetEditingHeader";
import SetEditingTermsAdder from "./components/SetEditingTermsAdder";
import SetSaveHandlerBtn from "./components/SetSaveHandlerBtn";
import { SetEditingContextProvider, useSetEditingContextSelector } from "./hooks/useSetEditingContext";
import { Flex, FlexColumn } from "@/components/Flex";
import { useMedia } from "@knowt/syncing/hooks/media/useMedia";
import { spacing } from "@/utils/spacing";
import SetEditingChatInterface from "./components/SetEditingChatInterface";
import br from "@/styles/breakpoints.module.css";
import FloatingFeedbackSelector from "@/components/Feedback/FloatingFeedbackSelector";
import { FeedbackEvents } from "@/components/Feedback/feedbackPopupContent";
import { FeedbackSubmitHandler } from "@/components/Feedback/FeedbackFlowContext";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { useLocalStorage } from "react-use";
import { ItemType } from "@knowt/syncing/graphql/schema";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import ChatAndMediaTabbedView from "@/features/AI/ChatAndMediaTabbedView/ChatAndMediaTabbedView";
import KaiChatFloatingIcon from "@/features/AI/KaiChat/components/KaiChatFloatingIcon";
import {
    useChatEditorCommunicator,
    ChatEditorCommunicatorProvider,
} from "@/features/AI/KaiChat/hooks/useChatEditorCommunicator";
import KaiCompleteUpgradeBanner, {
    KAI_COMPLETE_UPGRADE_BANNER_PREFIX_KEY,
} from "@/features/Payments/components/KaiCompleteUpgradeBanner";
import { dynamicKeyValueStore } from "@/utils/dynamicKeyValueStore";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { useEffect, useState } from "react";
import { useParams } from "next/navigation";
import SetEditingFlashcardList from "./components/SetEditingFlashcardList";
import TrackFlashcardSetViewEffect from "../FlashcardSetOverview/effects/TrackFlashcardSetViewEffect";
import { themeColors } from "@/utils/themeColors";

const FlashcardsEditingPageContent = () => {
    const params = useParams();

    const flashcardSet = useSetEditingContextSelector(s => s.flashcardSet);
    const isChatSplitSectionOpen = useSetEditingContextSelector(s => s.isChatSplitSectionOpen);
    const setIsChatSplitSectionOpen = useSetEditingContextSelector(s => s.setIsChatSplitSectionOpen);

    const stagePrompt = useChatEditorCommunicator(s => s.stagePrompt);
    const handleUnselect = useChatEditorCommunicator(s => s.handleUnselect);

    const { hasReachedAIUsage, user } = useCurrentUser();

    const upgradeEventStorageKey = KAI_COMPLETE_UPGRADE_BANNER_PREFIX_KEY + ItemType.FLASHCARDSET;

    const [isKaiCompleteUpgradeBannerOpen, setIsKaiCompleteUpgradeBannerOpen] = useState(
        !!user && !dynamicKeyValueStore.get(upgradeEventStorageKey) && !!hasReachedAIUsage("curPrompts")
    );

    useEffect(() => {
        setIsKaiCompleteUpgradeBannerOpen(
            !!user && !dynamicKeyValueStore.get(upgradeEventStorageKey) && !!hasReachedAIUsage("curPrompts")
        );
    }, [hasReachedAIUsage, upgradeEventStorageKey, user, user?.ai]);

    const { media } = useMedia({ mediaId: flashcardSet?.mediaId });

    const { mdDown } = useBreakPoints();

    const isShortScreenHeight = useMediaQuery(
        "(max-height: 800px) and (max-width: 900px), (max-height: 600px) and (max-width: 1170px), (max-height: 700px) and (max-width: 1400px), (max-height: 800px) and (min-width: 1410px)"
    );

    const handleToggleChatSplitSection = () => {
        if (isChatSplitSectionOpen) {
            setIsChatSplitSectionOpen(false);
            handleUnselect();
            stagePrompt({ prompt: "", inputQuestion: "" });
            return;
        }

        setIsChatSplitSectionOpen(true);
    };

    // FIXME Check why feedback shows up after refresh even though it's already submitted
    // INVESTIGATION:
    // - On SSR page load this feedback is shown because we don't have access to the local storage
    // - on client side navigation, we have access to the local storage and we can check if the feedback is already submitted
    // checkout https://linear.app/knowt/issue/WEB-1725/re-integrate-old-feedback-selector-flow-to-new-ai-flow
    const [hasFeedbackSubmitted, setHasFeedbackSubmitted] = useLocalStorage(
        `FLASHCARDS~FEEDBACK~${flashcardSet?.flashcardSetId}`,
        false
    );

    const feedbackSubmitHandler: FeedbackSubmitHandler = async ({ rating, category, followUp, reason }) => {
        await Mixpanel.track("AI Generated Flashcard Feedback Submitted", {
            rating,
            category,
            followUp,
            reason,
            noteId: flashcardSet?.noteId,
            mediaId: flashcardSet?.mediaId,
        });

        setHasFeedbackSubmitted(true);
    };

    const handleKaiCompleteUpgradeBannerClose = () => {
        dynamicKeyValueStore.set(upgradeEventStorageKey, "true");
        setIsKaiCompleteUpgradeBannerOpen(false);
    };

    return (
        <Flex
            style={{
                gap: spacing.SM,
                margin: "4rem 0",
                justifyContent: "space-between",
                position: "relative",
                width: "100%",
            }}
            className={br.mdDownColumn}>
            <FlexColumn
                className={br.mdDownWidth100}
                style={{
                    position: "relative",
                    width: isChatSplitSectionOpen ? "50%" : "100%",
                    willChange: "width",
                    maxWidth: isChatSplitSectionOpen ? "155rem" : "124rem",
                    margin: "auto",
                    height: "fit-content",
                    minHeight: "calc(100vh - var(--navbar-height) - 14rem)",
                }}>
                <SetEditingHeader />
                <KaiCompleteUpgradeBanner
                    isOpen={isKaiCompleteUpgradeBannerOpen}
                    onClose={handleKaiCompleteUpgradeBannerClose}
                    itemId={(params?.flashcardSetId as string) || ""}
                    itemType={ItemType.FLASHCARDSET}
                    style={{ width: "100%", marginBottom: "2rem" }}
                />
                <SetEditingFlashcardList />
                <SetEditingTermsAdder />
                <SetSaveHandlerBtn style={{ marginInline: "auto", marginBottom: "6rem" }} />
                <KaiChatFloatingIcon
                    className={br.smDownDisplayNone}
                    onClick={handleToggleChatSplitSection}
                    isOpen={isChatSplitSectionOpen}
                    style={{ marginLeft: "auto" }}
                />
            </FlexColumn>
            <ChatAndMediaTabbedView
                isOpen={isChatSplitSectionOpen}
                onClose={handleToggleChatSplitSection}
                media={media}
                disableUrlSync
                chatComponent={<SetEditingChatInterface />}
                className={br.mdDownWidth100}
                containerStyle={{
                    width: "50%",
                    minHeight: "60vh",
                    height: "fit-content",
                    overflowY: !mdDown && isShortScreenHeight ? "auto" : undefined,
                    maxHeight: isShortScreenHeight ? "80rem" : "calc(100vh - var(--navbar-height) - 6rem)",
                    visibility: isChatSplitSectionOpen ? "visible" : "hidden",
                    opacity: isChatSplitSectionOpen ? 1 : 0,
                    top: "calc(var(--navbar-height) + 4rem)",
                }}
                closeButtonProps={{
                    borderColor: themeColors.neutralWhite,
                }}
                isOwner={true}
            />
            <FloatingFeedbackSelector
                isOpen={!hasFeedbackSubmitted && !!flashcardSet?.isAI && !!flashcardSet?.draft}
                feedbackFor={FeedbackEvents.MEDIA_AI_FLASHCARD}
                id={flashcardSet?.flashcardSetId}
                title="What did you think of the AI-generated flashcards?"
                onSubmitHandler={feedbackSubmitHandler}
                direction="horizontal"
                autoCloseAfter={15000}
            />
        </Flex>
    );
};

const ConditionalFlashcardSetAdSlot = () => {
    const isChatSplitSectionOpen = useSetEditingContextSelector(s => s.isChatSplitSectionOpen);

    if (isChatSplitSectionOpen) return null;

    return <FallbackMainContainerAdSlot lowerVideoAd showUntilMd={false} />;
};

const FlashcardsEditingPage = ({ flashcardSetId }: { flashcardSetId: string }) => {
    return (
        <>
            <SetEditingContextProvider flashcardSetId={flashcardSetId} autoSave>
                <MainContainer style={{ marginBlock: "0rem 3rem", marginInline: "4rem 4rem", gap: "4rem" }}>
                    <InnerContainer style={{ maxWidth: "200rem" }}>
                        <ChatEditorCommunicatorProvider
                            itemType={ItemType.FLASHCARDSET}
                            itemId={flashcardSetId}
                            isOwner>
                            <TrackFlashcardSetViewEffect flashcardSetId={flashcardSetId} />
                            <FlashcardsEditingPageContent />
                        </ChatEditorCommunicatorProvider>
                    </InnerContainer>
                    <ConditionalFlashcardSetAdSlot />
                </MainContainer>
            </SetEditingContextProvider>
            <SetEditingPopups />
        </>
    );
};

export default FlashcardsEditingPage;
